/**
 * Error logger utility for production environment
 * Integrates with Sentry for error tracking
 */
import { captureException } from './sentry';

// Type definition for error data
interface ErrorData {
  message: string;
  stack?: string;
  componentStack?: string;
  additionalInfo?: Record<string, unknown>;
}

/**
 * Log error to console and to Sentry
 */
export function logError(error: Error, errorInfo?: React.ErrorInfo, additionalInfo?: Record<string, unknown>): void {
  // Always log to console in development
  if (import.meta.env.DEV) {
    console.error('Error caught:', error);
    if (errorInfo) {
      console.error('Component stack:', errorInfo.componentStack);
    }
    if (additionalInfo) {
      console.error('Additional info:', additionalInfo);
    }
  }

  // Send to Sentry in all environments (it will respect the enabled flag from initialization)
  const errorData: ErrorData = {
    message: error.message,
    stack: error.stack ?? undefined,
    componentStack: errorInfo?.componentStack ?? undefined,
    additionalInfo,
  };

  // Send to Sentry
  sendToErrorService(errorData, error);
}

/**
 * Send error data to Sentry
 */
function sendToErrorService(errorData: ErrorData, originalError: Error): void {
  // Send to Sentry with the original error and additional context
  captureException(originalError, {
    componentStack: errorData.componentStack,
    ...errorData.additionalInfo,
  });
} 